import { useState, useEffect, useMemo, useRef } from 'react';
import { opsApi } from '../../services/ops-api';
import type { TimeSlotSummaryRow } from '../../types/ops';
import { Clock, ChevronDown, X, Download } from 'lucide-react';

function shortCityName(city: string): string {
  const u = city.toUpperCase();
  if (u.includes('HÀ NỘI') || u.includes('HA NOI')) return 'Hà Nội';
  return city;
}

function CheckboxFilter({ label, options, selected, onChange, isDark, displayFn }: {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isDark: boolean;
  displayFn?: (val: string) => string;
}) {
  const display = displayFn || ((v: string) => v);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange(next);
  };

  const activeCount = selected.size;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1 border rounded px-2 py-1 text-xs w-full text-left focus:outline-none focus:border-cyan-500 cursor-pointer ${
          isDark
            ? `bg-slate-900 ${activeCount > 0 ? 'border-cyan-500/60 text-cyan-300' : 'border-slate-600 text-slate-400'}`
            : `bg-gray-50 ${activeCount > 0 ? 'border-cyan-500/60 text-cyan-600' : 'border-gray-300 text-gray-500'}`
        }`}
      >
        <span className="truncate flex-1">
          {activeCount === 0 ? `${label} (Tất cả)` : `${activeCount} đã chọn`}
        </span>
        {activeCount > 0 ? (
          <X
            className={`w-3.5 h-3.5 shrink-0 ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}
            onClick={e => { e.stopPropagation(); onChange(new Set()); }}
          />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        )}
      </button>

      {open && (
        <div className={`absolute z-50 top-full left-0 mt-1 w-max min-w-full max-h-60 overflow-y-auto border rounded shadow-xl ${isDark ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
          {options.map(opt => (
            <label
              key={opt}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer ${isDark ? 'text-white hover:bg-slate-700' : 'text-gray-900 hover:bg-gray-100'}`}
            >
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => toggle(opt)}
                className="accent-cyan-500 w-3.5 h-3.5"
              />
              <span className="truncate">{display(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TimeSlotsSummaryTable({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<TimeSlotSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterProvince, setFilterProvince] = useState<Set<string>>(new Set());
  const [filterStudio, setFilterStudio] = useState<Set<string>>(new Set());
  const [filterProgram, setFilterProgram] = useState<Set<string>>(new Set());

  useEffect(() => {
    opsApi.getTimeSlotsSummary()
      .then(data => { setRows(data); setError(null); })
      .catch(err => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const provinces = useMemo(() => [...new Set(rows.map(r => r.province || '(Chưa phân tỉnh)'))].sort(), [rows]);
  const studios = useMemo(() => {
    const base = filterProvince.size > 0
      ? rows.filter(r => filterProvince.has(r.province || '(Chưa phân tỉnh)'))
      : rows;
    return [...new Set(base.map(r => r.studioName))].sort();
  }, [rows, filterProvince]);
  const programs = useMemo(() => [...new Set(rows.map(r => r.programName))].sort(), [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterProvince.size > 0 && !filterProvince.has(r.province || '(Chưa phân tỉnh)')) return false;
      if (filterStudio.size > 0 && !filterStudio.has(r.studioName)) return false;
      if (filterProgram.size > 0 && !filterProgram.has(r.programName)) return false;
      return true;
    });
  }, [rows, filterProvince, filterStudio, filterProgram]);

  // Reset studio selections that are no longer valid when province filter changes
  useEffect(() => {
    if (filterStudio.size === 0) return;
    const validStudios = new Set(studios);
    const next = new Set([...filterStudio].filter(s => validStudios.has(s)));
    if (next.size !== filterStudio.size) setFilterStudio(next);
  }, [studios, filterStudio]);

  if (loading) {
    return <div className={`rounded-lg p-5 animate-pulse h-48 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} />;
  }

  if (error) {
    return (
      <div className={`rounded-lg p-5 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className={`rounded px-4 py-2 text-sm ${isDark ? 'bg-red-900/50 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-600'}`}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg p-5 space-y-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <h3 className={`flex items-center gap-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Clock className="h-5 w-5 text-blue-400" />
            Time Slots theo Studio &amp; Program (từ DB)
          </h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Số slot tính tự động từ giờ mở cửa, duration và break time trong database.
            {filtered.length !== rows.length && (
              <span className="ml-2 text-cyan-400">
                Hiển thị {filtered.length}/{rows.length} dòng
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            const header = ['Tỉnh', 'Studio', 'Training Program', 'Slots T2-T7', 'Slots CN'];
            const csvRows = [header.join(',')];
            for (const row of filtered) {
              csvRows.push([
                `"${(row.province || '(Chưa phân tỉnh)').replace(/"/g, '""')}"`,
                `"${row.studioName.replace(/"/g, '""')}"`,
                `"${row.programName.replace(/"/g, '""')}"`,
                row.weekdaySlots,
                row.sundaySlots,
              ].join(','));
            }
            const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'timeslots_summary.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${isDark ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
              <th className="text-left py-2 px-2 font-medium min-w-[160px]">
                <div className="space-y-1">
                  <div>Tỉnh</div>
                  <CheckboxFilter label="Tỉnh" options={provinces} selected={filterProvince} onChange={setFilterProvince} isDark={isDark} displayFn={shortCityName} />
                </div>
              </th>
              <th className="text-left py-2 px-2 font-medium min-w-[200px]">
                <div className="space-y-1">
                  <div>Studio</div>
                  <CheckboxFilter label="Studio" options={studios} selected={filterStudio} onChange={setFilterStudio} isDark={isDark} />
                </div>
              </th>
              <th className="text-left py-2 px-2 font-medium min-w-[140px]">
                <div className="space-y-1">
                  <div>Training Program</div>
                  <CheckboxFilter label="Program" options={programs} selected={filterProgram} onChange={setFilterProgram} isDark={isDark} />
                </div>
              </th>
              <th className="text-center py-2 px-2 font-medium">Slots T2-T7</th>
              <th className="text-center py-2 px-2 font-medium">Slots CN</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(row => (
              <tr key={`${row.studioName}-${row.programName}`} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
                <td className={`py-2 px-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{shortCityName(row.province || '(Chưa phân tỉnh)')}</td>
                <td className={`py-2 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.studioName}</td>
                <td className={`py-2 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.programName}</td>
                <td className="py-2 px-2 text-center text-emerald-400 font-mono">{row.weekdaySlots}</td>
                <td className="py-2 px-2 text-center text-amber-400 font-mono">{row.sundaySlots}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className={`py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Không có dữ liệu phù hợp</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
